import axios from "axios";

export interface CreateLocationInput {
  name?: string;
  latitude: number;
  longitude: number;
  address: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

/**
 * Summary of a single Map (mapGroup) at a Location — always represents
 * that Map's current version. Returned by GET /locations/:id/mulch-maps.
 */
export interface MapAtLocationSummary {
  mapGroupId: string;
  currentMapId: string;
  name: string | null;
  clientId: string | null;
  clientName: string | null;
  version: number;
  updatedAt: string;
  zoneCount: number;
  totalCuYd: number;
  /**
   * True when the underlying mulch_map row is soft-deleted (archived).
   * Only returned when the request opted in via includeArchived=true.
   */
  archived?: boolean;
}

/**
 * Create a Location or match an existing one at the same coordinates.
 * Does NOT create an RFQ (use createEstimate for that flow).
 */
export const createLocation = async (input: CreateLocationInput) => {
  try {
    const res: any = await axios.post(`/locations`, input);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

/**
 * List locations for the current company. Standard TableData-compatible
 * query shape: { search?, recordsPer?, pageNum?, sortBy?, sortDir? }.
 */
export const getLocations = async ({
  query = {},
  controller = null,
  excludeInterceptor = false,
}: any) => {
  const params: any = { params: query, excludeInterceptor };
  if (controller?.signal) {
    params.signal = controller.signal;
  }
  try {
    const res: any = await axios.get(`/locations`, params);
    return res?.data ? res.data : { items: [], total: 0 };
  } catch (e: any) {
    return { items: [], total: 0 };
  }
};

/**
 * Fetch a single Location by id. Backend enforces company scoping so this
 * is safe to call for any known id — unknown / other-company ids return
 * null (via the 404 interceptor bypass).
 */
export const getLocationById = async (
  id: string,
  opts?: { includeDeleted?: boolean },
): Promise<any | null> => {
  try {
    const url = opts?.includeDeleted
      ? `/locations/${id}?includeDeleted=true`
      : `/locations/${id}`;
    const res: any = await axios.get(url, {
      excludeInterceptor: true,
    } as any);
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * Session 25: partial update of a Location's editable fields.
 * Backend PATCH /locations/:id whitelists { siteNotes, name }.
 * siteNotes explicit null = clear.
 */
export const updateLocation = async (
  id: string,
  patch: { siteNotes?: string | null; name?: string },
): Promise<any | { error: string }> => {
  try {
    const res = await axios.patch(`/locations/${id}`, patch);
    return res?.data ?? null;
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Update failed." }
      : { error: "Update failed." };
  }
};

/**
 * List all Maps at a Location. Each entry summarizes one mapGroup's
 * current version. Used by the ExistingMapsChooser modal on ad-hoc
 * address open, and by the Location detail page (Commit 3).
 *
 * The 404 interceptor in App.tsx catches unknown locations globally,
 * so we opt out here — callers should treat empty [] and errors both
 * as "no maps to show" rather than triggering the app-wide error page.
 *
 * `includeArchived` returns soft-deleted Maps alongside active ones,
 * with `archived: true` on the summary. Used only by the Location
 * detail page's "Show archived" toggle.
 */
export const getMapsAtLocation = async (
  locationId: string,
  includeArchived = false,
): Promise<MapAtLocationSummary[]> => {
  try {
    const res: any = await axios.get(
      `/locations/${locationId}/mulch-maps`,
      {
        excludeInterceptor: true,
        params: includeArchived ? { includeArchived: "true" } : {},
      } as any,
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * All RFQs / Quotes / Projects / Invoices at a Location, with attached-Map
 * info. Feeds the "Records at this site" section on the Location detail
 * page — the primary path for reps hitting a repeat site (e.g. EMCOR) who
 * need to attach a Map to an existing record vs. draft a fresh one.
 */
export interface RecordsAtLocation {
  quoteRequests: any[];
  quotes: any[];
  projects: any[];
  invoices: any[];
  purchaseOrders: any[];
}

export const getRecordsAtLocation = async (
  locationId: string,
): Promise<RecordsAtLocation> => {
  try {
    const res: any = await axios.get(
      `/locations/${locationId}/records`,
      { excludeInterceptor: true } as any,
    );
    return {
      quoteRequests: res?.data?.quoteRequests ?? [],
      quotes: res?.data?.quotes ?? [],
      projects: res?.data?.projects ?? [],
      invoices: res?.data?.invoices ?? [],
      purchaseOrders: res?.data?.purchaseOrders ?? [],
    };
  } catch {
    return {
      quoteRequests: [],
      quotes: [],
      projects: [],
      invoices: [],
      purchaseOrders: [],
    };
  }
};

// ---------------------------------------------------------------------------
// Session 23 Location cleanup endpoints (frontend wiring Session 24).
// ---------------------------------------------------------------------------

/**
 * Reference counts across every downstream table (RFQ, Quote, Project,
 * Invoice via project.locationId, PO via project.locationId, Map, Profile).
 * Backend enforces isCompany; unknown ids return 404.
 *
 * Used by the delete confirmation flow: any count > 0 blocks hard delete
 * and the UI offers Archive as the safe alternative.
 */
export interface LocationReferenceCounts {
  quoteRequests: number;
  quotes: number;
  projects: number;
  invoices: number;
  purchaseOrders: number;
  mulchMaps: number;
  profiles: number;
  total: number;
}

export const getLocationReferenceCounts = async (
  locationId: string,
): Promise<LocationReferenceCounts | null> => {
  try {
    const res: any = await axios.get(
      `/locations/${locationId}/references`,
      { excludeInterceptor: true } as any,
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * Soft-delete a Location (hides it from the directory but preserves
 * downstream references — archive is reversible). Backend returns
 * { success: true } on success; failures throw and we return an
 * { error } shape for the UI to toast.
 */
export const archiveLocation = async (
  locationId: string,
): Promise<{ success: true } | { error: string }> => {
  try {
    const res: any = await axios.post(
      `/locations/${locationId}/archive`,
      {},
    );
    return res?.data ?? { success: true };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Archive failed." }
      : { error: "Archive failed." };
  }
};

export const restoreLocation = async (
  locationId: string,
): Promise<{ success: true } | { error: string }> => {
  try {
    const res: any = await axios.post(
      `/locations/${locationId}/restore`,
      {},
    );
    return res?.data ?? { success: true };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Restore failed." }
      : { error: "Restore failed." };
  }
};

/**
 * Hard-delete a Location. Backend returns 409 Conflict with blocking
 * reference counts if any downstream row still points at this Location.
 * Frontend should call getLocationReferenceCounts first and offer
 * Archive as the safe alternative when refs > 0.
 */
export const deleteLocation = async (
  locationId: string,
): Promise<
  | { success: true }
  | { error: string; blockingCounts?: LocationReferenceCounts }
> => {
  try {
    const res: any = await axios.delete(`/locations/${locationId}`);
    return res?.data ?? { success: true };
  } catch (e: any) {
    const data = e?.response?.data;
    if (e?.response?.status === 409 && data?.counts) {
      return {
        error: data.message || "Location has downstream references.",
        blockingCounts: data.counts,
      };
    }
    return { error: data?.message || "Delete failed." };
  }
};

/**
 * Cluster of near-duplicate Locations returned by GET /locations/duplicates.
 * Clustered by normalized address line1 (case-insensitive, trimmed) OR by
 * coords within ~25m (haversine gate on top of a 3-decimal bounding box).
 * Each cluster is surfaced to the UI so the rep can pick a survivor and
 * merge the others.
 */
export interface DuplicateLocationMember {
  id: string;
  name: string | null;
  latitude: number | null;
  longitude: number | null;
  address: {
    line1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  } | null;
  createdAt: string;
  recordCount: number;
  mapCount: number;
  profileCount: number;
}

export interface DuplicateLocationCluster {
  key: string;
  reason: "same_address" | "same_coords";
  locations: DuplicateLocationMember[];
}

export const findDuplicateLocations = async (): Promise<
  DuplicateLocationCluster[]
> => {
  try {
    const res: any = await axios.get(`/locations/duplicates`, {
      excludeInterceptor: true,
    } as any);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * Impact summary returned by the merge endpoint. Same shape for dry-run
 * (?dryRun=1) and execute — the dry-run flag just tells the backend to
 * compute the plan and skip the commit.
 *
 * Recall on shape: backend returns { dryRun, sourceId, targetId, moved, warnings }.
 * `moved` contains the per-table counts including profile relocate/merge
 * numbers. `warnings` is a soft-signal channel for the UI to display (e.g.
 * profiles the merge can't reconcile).
 */
export interface MergeLocationsImpact {
  dryRun: boolean;
  sourceId: string;
  targetId: string;
  moved: {
    quoteRequests: number;
    quotes: number;
    projects: number;
    mulchMaps: number;
    profilesRelocated: number;
    profilesMerged: number;
    recordProfileFksRewritten: number;
  };
  warnings: string[];
}

export const mergeLocations = async (
  sourceId: string,
  targetId: string,
  options: { dryRun?: boolean } = {},
): Promise<MergeLocationsImpact | { error: string }> => {
  try {
    const res: any = await axios.post(
      `/locations/${sourceId}/merge`,
      { targetId },
      { params: options.dryRun ? { dryRun: "1" } : {} } as any,
    );
    return res?.data ?? { error: "Merge returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Merge failed." }
      : { error: "Merge failed." };
  }
};
